<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import PhoneNumber from "@/components/phones/PhoneNumber.vue";
import TruckStatus from "@/components/statuses/TruckStatus.vue";
import UserStatus from "@/components/statuses/UserStatus.vue";
import TruckEquipmentBlock from "@/views/fleets/components/EquipmentBlock.vue";
import TruckContactBlock from "@/views/fleets/components/ContactBlock.vue";
import TruckNotes from "@/views/fleets/components/TruckNotes.vue";
import ChangeTruckStatusAction from "@/views/fleets/components/ChangeTruckStatusAction.vue";
import ReserveTruckAction from "@/views/fleets/components/ReserveTruckAction.vue";
import OmniTable from "@/components/omni/OmniTable.vue";
import ExpandButton from "@/components/buttons/ExpandButton.vue";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import { useAppStore } from "@/store/app.store";
import TruckMember from "@/components/truck/TruckMember.vue";
import { useBaseView, useDateTime, useFilters, usePopup, useTruck } from "@/composables/index";
import { useGoogleMap } from "@/composables/useGoogleMap";
import TruckContactPersonRoles from "@/views/fleets/components/TruckContactPersonRoles.vue";
import { useSocketEvent } from "@/composables/useSocketEvent";
import { TruckStatus as TruckStatusEnum } from "@/constants/truck";
import { DriverStatus, DriverStatusOnHold, OwnerStatus } from "@/constants/user";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import type { TruckReservedWSData } from "@/api/trucks/dto/ws/reserved";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import TruckExclusiveIcon from "@/components/icons/TruckExclusiveIcon.vue";
import { API } from "@/api/api";
import { printSizes } from "@/utils/sizes";
import { joinNonEmpty } from "@/utils/string";
import { useI18n } from "vue-i18n";
import type { TrucksListResponseDto } from "@/api/trucks/dto/response/trucks-list";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";

const props = withDefaults(
  defineProps<{
    availableColumns?: string[];
    searchParams?: Record<string, any>;
    compact?: boolean;
  }>(),
  {
    availableColumns: () => [],
    compact: false,
  },
);

const emit = defineEmits<{
  (e: "params", value: any): void;
  (e: "handle-error", value: any): void;
}>();

const api = new API();
const { t, tm } = useI18n();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const { dateTZ } = useDateTime();
const { miles, upper, mdash } = useFilters();
const { params, pagination, hasAccess } = useBaseView();
const { getTruckType, getReservationTime, getReservedBy, doorTypeToString } = useTruck();
const { getRouteUrl, getPlaceUrl } = useGoogleMap();
const { openPopup, popupWidth, popupHeight, popupX, popupY } = usePopup();

const data = ref<TrucksListResponseDto>({ items: [], total: 0, page: 1 });
const sortBy = ref([{ key: "distance", order: "asc" }]);
const expanded = ref<any[]>([]);
const loading = ref(false);
const archived = ref(false);

const isTrucksSearch = computed(() => route.name === "trucks-search");
const hasAccessToReserve = computed(() => hasAccess(DispatcherPermissionValue.TrucksReserve));
const hasAccessToView = computed(() => hasAccess(DispatcherPermissionValue.TrucksInfo));

const headers = computed(() => {
  const allHeaders: Record<string, any> = {
    name: { key: "name", title: t("general.tt"), sortable: false, class: "col-name" },
    status: {
      key: "status",
      title: t("general.status"),
      sortable: false,
      align: "center",
      class: "col-status",
    },
    driver: {
      key: "driver",
      title: t("general.driver-info"),
      sortable: false,
    },
    size: {
      key: "size",
      title: t("trucks.dims-payload"),
      sortable: false,
      align: "right",
      class: "col-dims",
    },
    available: {
      key: "available",
      title: t("trucks.available-location"),
      sortable: false,
      class: "col-location",
    },
    current: {
      key: "current",
      title: t("trucks.current-location"),
      sortable: false,
      class: "col-location",
    },
    contact: {
      key: "contact",
      title: t("trucks.contact-person"),
      sortable: false,
    },
    distance: {
      key: "distance",
      title: t("trucks.distance"),
      sortable: false,
      class: "col-distance",
    },
    notes: {
      key: "notes",
      title: t("trucks.notes"),
      sortable: false,
      class: "notes",
    },
  };

  const result = props.availableColumns.map((name) => allHeaders[name]);
  result.push({
    title: "Actions",
    key: "actions",
    align: "left",
    class: "col-actions",
    sortable: false,
  });

  return result;
});

const exclusiveTrucks = computed(() => data.value.items.filter((truck) => truck.exclusive));
const ordinaryTrucks = computed(() => data.value.items.filter((truck) => !truck.exclusive));

const sortedTrucks = computed(() => {
  if (!isTrucksSearch.value) {
    return data.value.items;
  }

  return [...exclusiveTrucks.value, ...ordinaryTrucks.value];
});

const hasExclusiveDividers = computed(() => {
  return isTrucksSearch.value && exclusiveTrucks.value.length > 0;
});

function printDoorSizes(truck: TruckDto): string {
  return joinNonEmpty(
    [
      t("trucks.door-dims", { sizes: printSizes(truck.trailer?.doorWidth, truck.trailer?.doorHeight) }),
      truck.trailer?.doorType ? doorTypeToString(truck) : null,
    ],
    ", ",
  );
}

function printCompaniesNumbers(truck: TruckDto): string {
  return joinNonEmpty(truck.companiesNumbers?.map((item) => item.number) ?? [], ", ");
}

function isFirstTruckOfGroup(truck: TruckDto, index: number) {
  const isExclusiveFirst = exclusiveTrucks.value.findIndex((item) => item.id === truck.id) === 0;
  const isOrdinaryFirst = ordinaryTrucks.value.findIndex((item) => item.id === truck.id) === 0;

  return isExclusiveFirst || isOrdinaryFirst;
}

function sortTrucks() {
  const sortingField = props.searchParams?.byHomeAddress ? "homeDistance" : "pickupDistance";

  data.value.items.sort((a: any, b: any) => {
    if (a[sortingField] < b[sortingField]) return -1;
    if (a[sortingField] > b[sortingField]) return 1;
    return 0;
  });
}

async function refresh(): Promise<boolean> {
  loading.value = true;
  const requestParams: Record<string, any> = { ...props.searchParams, ...params.value };
  requestParams.archived = archived.value;

  if (isTrucksSearch.value) {
    requestParams.pageSize = -1;
  }

  try {
    const resp = await api.trucks.findTrucks(requestParams);

    if (resp.success && resp.data) {
      data.value = resp.data;
      emit("params", (resp.data as any).params);
      sortTrucks();
    }

    if (resp.errorData?.detail) {
      emit("handle-error", resp.errorData.detail);
      data.value = { items: [], total: 0, page: 1 };
    } else {
      emit("handle-error", null);
    }

    return resp.success;
  } catch (error) {
    console.error("Error loading trucks", error);
    return false;
  } finally {
    loading.value = false;
  }
}

async function init() {
  if (!props.searchParams) {
    return;
  }

  params.value.page = 1;

  if (!isTrucksSearch.value) {
    await refresh();
  }

  if (props.searchParams.location) {
    await refresh();
  }
}

async function loadExtraData(truck: any) {
  if (truck.coordinators === undefined) {
    const resp = await api.users.findOwnerCoordinators(truck.ownerId);
    if (resp.success) {
      truck.coordinators = resp.data;
    }
  }

  if (truck.owner && truck.owner.isDriver && truck.owner.licenseNumber === undefined) {
    const resp = await api.users.findDriverById(truck.owner.id);
    if (resp.success) {
      truck.owner = { ...truck.owner, ...resp.data };
    }
  }

  if (truck.driverId && truck.driver.licenseNumber === undefined) {
    const resp = await api.users.findDriverById(truck.driverId);
    if (resp.success) {
      truck.driver = { ...truck.driver, ...resp.data };
    }
  }

  if (truck.driver2Id && truck.driver2.licenseNumber === undefined) {
    const resp = await api.users.findDriverById(truck.driver2Id);
    if (resp.success) {
      truck.driver2 = { ...truck.driver2, ...resp.data };
    }
  }
}

async function expand(itemId: any) {
  if (expanded.value.length === 1 && expanded.value[0] === itemId) {
    expanded.value = [];
  } else {
    expanded.value = [itemId];

    const truck = data.value.items.find((t: any) => t.id === itemId);
    await loadExtraData(truck);
  }
}

function updateTruck(truck: any) {
  const currentTruck = data.value.items.find((t: any) => t.id === truck.id);
  if (!currentTruck) return;

  const { pickupDistance, ...rest } = truck;

  Object.assign(currentTruck, rest);

  if (currentTruck.driver && truck.driver) {
    currentTruck.driver.note = truck.driver.note;
    currentTruck.driver.driverNoteText = truck.driver.driverNoteText;
  }

  if (currentTruck.driver2 && truck.driver2) {
    currentTruck.driver2.note = truck.driver2.note;
    currentTruck.driver2.driverNoteText = truck.driver2.driverNoteText;
  }
}

function handleReservedTruckWS(reservedTruckWS: TruckReservedWSData) {
  data.value.items = data.value.items.map((truck: any) => {
    if (truck.id === reservedTruckWS.truckId) {
      return {
        ...truck,
        reservedById: reservedTruckWS.reservedById,
        reservedTill: reservedTruckWS.reservedTill,
      };
    }

    return truck;
  });
}

async function openMapSettingsWindow() {
  if ("getScreenDetails" in window && "isExtended" in screen && screen.isExtended) {
    await (window as any).getScreenDetails();
  }
  const routeData = router.resolve({ name: "map-position" });
  openPopup({
    url: routeData.href,
    title: "PopupSettings",
    w: popupWidth(),
    h: popupHeight(),
    x: popupX(),
    y: popupY(),
  });
}

function openRouteMap(from: any, to: any) {
  const href = getRouteUrl(from, to);
  openPopup({
    url: href,
    title: "RouteMap",
    w: popupWidth(),
    h: popupHeight(),
    x: popupX(),
    y: popupY(),
  });
}

function openPlaceMap(place: any) {
  const href = getPlaceUrl(place, 6);
  openPopup({
    url: href,
    title: "PlaceMap",
    w: popupWidth(),
    h: popupHeight(),
    x: popupX(),
    y: popupY(),
  });
}

function dateToTimezone(date: any, timezone: any) {
  return dateTZ(date, timezone);
}

function viewDistanceRoute(item: any) {
  if (props.searchParams?.byHomeAddress && item.driver.addressLocation) {
    openRouteMap(item.driver.addressLocation, data.value.pickupLocation);
  } else if (item.availableLocation?.coordinates) {
    openRouteMap(item.availableLocation.coordinates, data.value.pickupLocation);
  }
}

function getCellClass(cell: any) {
  if (cell.column.key !== "name") {
    return "";
  }

  const onHoldChecks = [
    () => cell.item.status === TruckStatusEnum.OnHold,
    () => cell.item.owner.driverStatus === DriverStatus.OnHold,
    () => cell.item.owner.ownerStatus === OwnerStatus.OnHold,
    () => DriverStatusOnHold.includes(cell.item.driver?.driverStatus),
    () => DriverStatusOnHold.includes(cell.item.driver2?.driverStatus),
  ];

  const hasOnHold = onHoldChecks.some((check) => check());

  return hasOnHold ? "border-left border-left-danger border-left-width-3" : "";
}

function getCellProps(cell: any) {
  return {
    class: getCellClass(cell),
  };
}

function hasReserved(item: any) {
  return item.reservedById && getReservationTime(item) > -1;
}

async function updatePage(page: number) {
  params.value.page = page;
  await refresh();
}

async function updatePageSize(size: number) {
  params.value.pageSize = size;
  await refresh();
}

watch(
  () => props.searchParams,
  async () => {
    params.value.page = 1;
    await refresh();
  },
);

watch(archived, () => {
  refresh();
});

useSocketEvent("truck:reserved", (payload) => {
  handleReservedTruckWS(payload);
});

useSocketEvent("socket:recovered", () => {
  if (!props.searchParams) {
    return true;
  }

  if (isTrucksSearch.value && !props.searchParams.location) {
    return true;
  }

  return refresh();
});

onMounted(async () => {
  await init();
});
</script>

<template>
  <div v-if="data.pickupAddress" class="text-body-2 mb-2 d-flex align-center">
    {{ data.pickupAddress }}
    <div v-if="data.pickupDeliveryDistance && data.pickupDeliveryDistance > 0">
      &rarr; {{ data.deliveryAddress }} &bsim; {{ miles(data.pickupDeliveryDistance) }} miles
      <a
        class="text-decoration-none text-primary ml-2 cursor-pointer text-uppercase font-weight-500 font-size-12"
        target="_blank"
        @click="openRouteMap(data.pickupLocation, data.deliveryLocation)"
      >
        <v-icon color="primary" size="small">mdi-open-in-new</v-icon>
        <span> View on route</span>
      </a>
    </div>

    <a
      v-else
      class="d-flex align-center text-decoration-none text-primary ml-2 cursor-pointer text-uppercase font-weight-500 font-size-12"
      target="_blank"
      @click="openPlaceMap(data.pickupLocation)"
    >
      <v-icon class="mr-1" color="primary" size="small">mdi-open-in-new</v-icon>
      <span> View on map</span>
    </a>

    <div class="d-flex justify-space-between align-center ml-3 font-size-12">
      <div class="cursor-pointer" @click="openMapSettingsWindow">
        <v-icon color="primary" size="small">mdi-cog</v-icon>
      </div>
    </div>
  </div>

  <omni-table
    v-model:archived="archived"
    v-model:expanded="expanded"
    v-model:sort-by="sortBy"
    :cell-props="getCellProps"
    :compact="compact"
    :footer-props="{ 'items-per-page-options': isTrucksSearch ? [] : pagination.itemsPerPage }"
    :headers="headers"
    :hide-default-footer="isTrucksSearch"
    :items="sortedTrucks"
    :items-length="data.total"
    :items-per-page="params.pageSize"
    :loading="loading"
    :mobile-breakpoint="0"
    :show-divider-before="hasExclusiveDividers ? isFirstTruckOfGroup : undefined"
    show-archive
    @update:page="updatePage"
    @update:items-per-page="updatePageSize"
  >
    <template #row-divider="{ item }">
      <div v-if="item.exclusive" class="d-flex align-center font-size-12" data-qa="exclusive-trucks-divider">
        <truck-exclusive-icon />
        <span class="font-weight-500 text-grey-darken-2">
          {{ t("trucks.exclusive-trucks-count", { count: exclusiveTrucks.length }) }}
        </span>
      </div>
      <div v-else class="font-size-12 font-weight-500 text-grey-darken-2" data-qa="other-trucks-divider">
        {{ t("trucks.all-other-trucks", { count: ordinaryTrucks.length }) }}
      </div>
    </template>

    <template #[`header.distance`]>
      <div class="d-flex align-center justify-space-between">
        <span>Distance</span>

        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon color="primary" size="small" v-bind="tooltipProps">mdi-information-outline</v-icon>
          </template>

          Distance from the available location to the city <br />
          Distance from the home location to the city
        </v-tooltip>
      </div>
    </template>

    <template #[`header.status`]>
      <div class="d-flex align-center justify-space-between">
        <span>Status</span>

        <v-tooltip location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon size="small" v-bind="tooltipProps">mdi-information-outline</v-icon>
          </template>

          <span class="font-weight-500">{{ t("trucks.tooltip-status-info-title") }}</span>
          <br />
          <template v-for="(point, index) in tm('trucks.tooltip-status-info-points')" :key="index">
            • {{ point }} <br />
          </template>

          <div class="font-weight-500 mt-2">{{ t("trucks.tooltip-status-bottom-title") }}</div>
        </v-tooltip>
      </div>
    </template>

    <template #[`item.name`]="{ item }">
      <div class="d-flex align-center">
        <expand-button
          :expanded="expanded.length === 1 && expanded[0] === item.id"
          class="mr-3"
          color="primary"
          size="xsmall"
          @click="expand(item.id)"
        />
        <div class="w-100">
          <div class="d-flex w-100 align-center justify-space-between mb-1">
            <div class="d-flex align-center">
              <truck-exclusive-icon v-if="item.exclusive" />

              <router-link
                :to="{ name: 'truck-view', params: { id: item.id } }"
                class="text-decoration-none text-primary"
                data-qa="truck-number"
              >
                {{ item.number }}
              </router-link>
            </div>
          </div>
          <div
            v-if="item.companiesNumbers?.length"
            class="text-grey-darken-3 font-size-12 mb-1"
            data-qa="truck-companies-numbers"
          >
            {{ printCompaniesNumbers(item) }}
          </div>
          <div class="text-grey-darken-2 font-size-12" data-qa="truck-type">{{ getTruckType(item.type) }}</div>
        </div>
      </div>
    </template>
    <template #[`item.status`]="{ item }">
      <div class="col-status" data-qa="truck-status">
        <truck-notes
          v-if="
            item.note ||
            (item.driver && (item.driver.driverNoteText || item.driver.note)) ||
            (item.driver2 && (item.driver2.driverNoteText || item.driver2.note))
          "
          :truck="item"
        />
        <change-truck-status-action :truck="item" @success="updateTruck">
          <truck-status :truck="item" class="pointer" />
        </change-truck-status-action>
      </div>
    </template>
    <template #[`item.driver`]="{ item }">
      <div v-if="item.driver">
        <truck-member :member="item.driver" driver-name-class="mb-2" />
      </div>
    </template>
    <template #[`item.distance`]="{ item }">
      <div class="d-flex justify-space-between">
        <div :class="`${searchParams?.byHomeAddress ? 'flex-column-reverse' : 'flex-column'}`" class="d-flex">
          <div class="d-flex text-no-wrap align-center distance-block">
            <v-tooltip>
              <template #activator="{ props: tooltipProps }">
                <div
                  :class="{ 'font-weight-bold': !searchParams?.byHomeAddress }"
                  class="d-flex align-center"
                  v-bind="tooltipProps"
                >
                  <v-icon :color="!searchParams?.byHomeAddress ? 'black' : 'grey-darken-2'" class="mr-1" size="small">
                    mdi-cube-outline
                  </v-icon>
                  <span class="font-size-12 text-grey-darken-3 mr-2" data-qa="distance-value">
                    {{ miles(item.pickupDistance, 0) }} miles
                  </span>
                </div>
              </template>

              Distance from the available location to the city
            </v-tooltip>
          </div>
          <div
            v-if="item.homeDistance !== null && item.homeDistance !== undefined"
            class="d-flex btext-no-wrap align-center distance-block"
          >
            <v-tooltip>
              <template #activator="{ props: tooltipProps }">
                <div
                  :class="{ 'font-weight-bold': searchParams?.byHomeAddress }"
                  class="d-flex align-center"
                  v-bind="tooltipProps"
                >
                  <v-icon :color="searchParams?.byHomeAddress ? 'black' : 'grey-darken-2'" class="mr-1" size="small"
                    >mdi-home-outline
                  </v-icon>
                  <span class="font-size-12 text-grey-darken-3 mr-2">{{ miles(item.homeDistance, 0) }} miles</span>
                  <a
                    v-if="item.deliveryLocation && item.driver && item.driver.addressLocation"
                    :class="item.id"
                    class="text-decoration-none text-primary font-size-11 cursor-pointer"
                    target="_blank"
                    @click="openRouteMap(item.deliveryLocation, item.driver.addressLocation)"
                  >
                    View route
                  </a>
                </div>
              </template>

              Distance from the home location to the city
            </v-tooltip>
          </div>
        </div>

        <a
          v-if="data.pickupLocation"
          class="text-decoration-none text-primary cursor-pointer font-size-11"
          target="_blank"
          @click="viewDistanceRoute(item)"
        >
          View route
        </a>
      </div>
    </template>
    <template #[`item.contact`]="{ item }">
      <div v-if="item.driver && item.driver.contactPerson">
        <div class="d-flex align-center mb-2">
          <span class="currency-icon mr-2 small" />
          <truck-contact-person-roles
            v-if="item.driver.contactPerson"
            :contact-person="item.driver.contactPerson"
            data-qa="contact-person-role"
          />
          <router-link
            :to="{ name: 'user-view', params: { id: item.driver.contactPersonId } }"
            class="text-primary text-decoration-none mr-2 line-height-1"
            data-qa="contact-person-name"
          >
            {{ item.driver.contactPerson.name }}
          </router-link>
          <user-status :status="item.driver.driverStatus" data-qa="contact-person-status" />
        </div>

        <div class="d-flex align-center">
          <phone-number
            :number="item.driver.contactPerson.phone"
            class="font-size-12 text-decoration-none text-primary"
          />
          <span
            class="ml-2 text-uppercase bg-grey-lighten-4 rounded text-grey-darken-1 pr-1 pl-1 caption type-language"
          >
            {{ upper(item.driver.language) }}
          </span>
        </div>
      </div>
    </template>
    <template #[`item.notes`]="{ item }">
      <truck-notes :truck="item" @update="updateTruck" />
      <div v-if="item.driver" class="text-grey-darken-3">
        {{ mdash(item.driver.driverNote) }}
      </div>
      <div v-if="item.note" class="text-grey-darken-3">
        {{ item.note }}
      </div>
    </template>
    <template #[`item.size`]="{ item }">
      <div v-if="item.trailer" class="text-right">
        <div class="text-grey-darken-3" data-qa="truck-trailer-dims">
          {{ printSizes(item.trailer.length, item.trailer.width, item.trailer.height) }}
        </div>
        <div
          v-if="item.trailer.doorWidth || item.trailer.doorHeight"
          class="text-grey-darken-1"
          data-qa="truck-trailer-door-dims"
        >
          {{ printDoorSizes(item) }}
        </div>
        <div class="text-grey-darken-2 font-size-12">{{ item.trailer.payload }} lbs</div>
      </div>
      <template v-else> &mdash;</template>
    </template>
    <template #[`item.available`]="{ item }">
      <truck-location-info
        v-if="item.availableLocation"
        :current-location="item.currentLocation"
        :current-location-time="item.currentLocationTime"
        :location="item.availableLocation"
        :show-info-menu="isTrucksSearch"
        :show-tooltip="false"
        :time="item.availableDate"
        hide-dot
        show-changed-by
      />
    </template>
    <template #[`item.current`]="{ item }">
      <truck-location-info :location="item.currentLocation" :time="item.currentLocationTime" show-indicator />
    </template>
    <template #[`item.actions`]="{ item }">
      <div class="d-flex align-center">
        <permission-tooltip :permission="DispatcherPermissionValue.TrucksEdit">
          <template #activator="{ disabled }">
            <v-btn
              :disabled="disabled"
              :to="{ name: 'truck-edit', params: { id: item.id } }"
              class="pa-1 table-action mr-2"
              flat
              size="small"
              variant="text"
            >
              <v-icon class="text-primary">mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
          {{ t("permission.edit-truck") }}
        </permission-tooltip>

        <omni-tooltip :disabled="hasAccessToView">
          <template #activator>
            <v-btn
              :disabled="!hasAccessToView"
              :to="{ name: 'truck-view', params: { id: item.id } }"
              class="pa-1 table-action mr-2"
              flat
              size="small"
              variant="text"
            >
              <v-icon class="text-primary">mdi-eye-outline</v-icon>
            </v-btn>
          </template>

          {{ t("permission.view-truck") }}
        </omni-tooltip>

        <omni-tooltip v-if="hasReserved(item)" top>
          <template #activator>
            <reserve-truck-action
              :disabled="item.reservedById !== appStore.dispatcher?.id"
              :truck="item"
              @success="updateTruck"
            >
              <v-btn
                :disabled="item.reservedById !== appStore.dispatcher?.id"
                class="font-weight-600"
                color="success"
                size="small"
                variant="flat"
              >
                RESERVED, {{ getReservationTime(item) }}m
              </v-btn>
            </reserve-truck-action>
          </template>
          <span>
            Reserved by {{ getReservedBy(item) }} until
            {{ dateToTimezone(item.reservedTill, appStore.dispatcher?.timezone) }}
          </span>
        </omni-tooltip>

        <omni-tooltip v-else :disabled="hasAccessToReserve">
          <template #activator>
            <reserve-truck-action :disabled="!hasAccessToReserve" :truck="item" @success="updateTruck">
              <v-btn
                :disabled="!hasAccessToReserve"
                class="text-uppercase font-weight-600"
                color="primary"
                size="small"
                variant="flat"
                >Reserve</v-btn
              >
            </reserve-truck-action>
          </template>

          {{ t("permission.reserve-truck") }}
        </omni-tooltip>
      </div>
    </template>
    <template #expanded-row="{ columns, item }">
      <tr>
        <td :colspan="columns.length" class="dop-row">
          <div class="bg-grey-lighten-4 pt-3 pr-4 pb-3 pl-4 dop-row__inner">
            <div class="blocks-items d-flex flex-wrap">
              <truck-equipment-block :truck="item" />

              <truck-contact-block
                v-if="item.owner && ![item.driverId, item.driver2Id].includes(item.ownerId)"
                :is-contact-person="
                  item.driver && item.driver.contactPersonId && item.ownerId === item.driver.contactPersonId
                "
                :label="t('drivers.owner')"
                :user="item.owner"
                owner
                show-certificates
              />

              <truck-contact-block
                v-if="item.driver"
                :is-contact-person="item.driver.contactPersonId && item.driverId === item.driver.contactPersonId"
                :label="item.driver.id === item.owner.id ? t('drivers.owner-driver') : t('drivers.driver')"
                :user="item.driver"
                show-certificates
              />
              <truck-contact-block
                v-if="item.driver2"
                :is-contact-person="item.driver2Id === item.driver.contactPersonId"
                :label="item.driver2.id === item.owner.id ? t('drivers.owner-driver') : t('drivers.second-driver')"
                :user="item.driver2"
                show-certificates
              />

              <truck-contact-block
                v-for="coordinator in item.coordinators"
                :key="coordinator.userId"
                :full-access="coordinator.fullAccess"
                :is-contact-person="coordinator.user.id === item.driver.contactPersonId"
                :show-status="false"
                :user="coordinator.user"
                label="Coordinator"
              />
            </div>
          </div>
        </td>
      </tr>
    </template>
  </omni-table>
</template>

<style lang="scss" scoped>
.dop-row {
  border-right: 0 !important;
  padding: 0 0 12px 0 !important;
  background: #f8f8fb;

  &__inner {
    box-shadow: 0 4px 4px -1px rgba(0, 0, 0, 0.16);
  }
}

.blocks-items {
  margin: 0 -8px;
}

.table-action {
  width: auto !important;
  min-width: auto !important;
  padding: 0 2px !important;
  background: transparent !important;

  &:not(:last-child) {
    margin-right: 10px;
  }
}

.location {
  line-height: 17px;
}

.table-status {
  white-space: nowrap;
  display: inline-block;
  line-height: 16px !important;
}

.type-language {
  line-height: 12px;
  font-size: 11px;
  padding: 2px 0;
}

.line-height-1 {
  line-height: 1 !important;
}

.status-item {
  font-size: 9px;
  line-height: 16px;
  height: 16px !important;
  border-radius: 100px;
  padding: 0 2px !important;
  min-width: auto !important;
}

.currency-icon {
  width: 18px;
  flex: 0 0 18px;
  height: 18px;
  font-size: 13px;
  display: inline-block;
  text-align: center;
  border-radius: 50%;
  background: url(@/assets/images/currency-icon.svg) no-repeat;
  background-size: 18px;

  &.small {
    width: 14px;
    flex: 0 0 14px;
    height: 14px;
    background-size: 14px;
  }
}
</style>

<style lang="scss" scoped>
.col-name {
  min-width: 160px;
  width: 10%;
}

thead .col-status {
  width: 6%;
  min-width: 145px;
}

.col-info {
  width: 15%;
  min-width: 250px;
}

.col-dims,
.col-payload {
  width: 1%;
  min-width: 150px;
}

.col-location {
  width: 15%;
  min-width: 165px;
}

.col-actions {
  width: 1%;
  min-width: 170px;
}

.col-distance {
  width: 11.8%;
  min-width: 180px;
}

.v-application .location-block .caption {
  font-size: 11px !important;
  line-height: 12px;
}

.font-size-11 {
  font-size: 11px;
}
</style>
